import { Box, Flex, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import CommonTabSwitcher from 'components/commonTabSwitcher/CommonTabSwitcher';
import {
  MERCHANT_SCOPE,
  MerchantStationRevenueColor,
  STATION_DETAIL,
} from 'utils/StaticData';
import MerchantRevenueStationGraph from './MerchantRevenueStationGraph';

// Legend entries beyond this are folded into a "+N more" note. A venue with 40
// stations would otherwise push the chart off the card.
const MAX_LEGEND_ENTRIES = 12;

const SCOPE_TABS = [
  { label: 'Top 15', shortLabel: 'Top 15' },
  { label: 'All merchants', shortLabel: 'All' },
];

const DETAIL_TABS = [
  { label: 'Merchant total', shortLabel: 'Total' },
  { label: 'All stations', shortLabel: 'Stations' },
];

/**
 * RAT-1926: the "Gross Revenue Per Station" card.
 *
 * Two independent selectors decide how much of the dataset is drawn. The heavy
 * combination (all merchants with all their stations) is only ever fetched when
 * someone picks it, so the default dashboard load stays as fast as RAT-1804
 * made it. Both selectors reset to their defaults on every page load.
 */
const GrossRevenuePerStationCard = ({
  report,
  isLoading,
  currency,
  merchantScope,
  stationDetail,
  onScopeChange,
  onDetailChange,
  isMerchantFiltered,
}) => {
  // The response is the source of truth: a single merchant always comes back
  // with its stations, whichever breakdown was requested. Fall back to the
  // requested mode while the first response is still in flight.
  const isStacked = report
    ? report.detail === STATION_DETAIL.ALL
    : stationDetail === STATION_DETAIL.ALL;

  // A single merchant puts stations on the X axis, so neither selector has
  // anything left to switch between.
  const isSingleMerchant = report?.merchantCount === 1;

  // In single-merchant mode the X axis already IS the stations, so a
  // "Station 1..N" legend would just repeat the axis labels.
  const legendEntries = useMemo(() => {
    if (isSingleMerchant) return [];
    if (!isStacked || !report?.maxStationsPerMerchant) return [];
    return Array.from(
      { length: Math.min(report.maxStationsPerMerchant, MAX_LEGEND_ENTRIES) },
      (_, index) => ({
        label: `Station ${index + 1}`,
        color:
          MerchantStationRevenueColor[
            index % MerchantStationRevenueColor.length
          ],
      }),
    );
  }, [isStacked, isSingleMerchant, report?.maxStationsPerMerchant]);

  const hiddenLegendCount =
    (report?.maxStationsPerMerchant ?? 0) - legendEntries.length;

  const summary = useMemo(() => {
    if (!report) return '';
    const merchantLabel = `${report.merchantCount} ${
      report.merchantCount === 1 ? 'merchant' : 'merchants'
    }`;
    if (!isStacked) return merchantLabel;
    return `${merchantLabel} - ${report.stationCount} ${
      report.stationCount === 1 ? 'station' : 'stations'
    }`;
  }, [report, isStacked]);

  return (
    <Box
      w="full"
      h="full"
      p={{ base: '4', md: '5' }}
      bg="white"
      border="1px solid #D9D9D9"
      borderRadius="20px"
      boxShadow="0px 0px 20px rgba(32, 57, 70, 0.06)"
    >
      <Text
        fontSize={{ base: '16px', md: '24px' }}
        fontWeight="semibold"
        mb={{ base: '3', md: '4' }}
        color="primary"
      >
        Gross Revenue Per Station
      </Text>

      {!isSingleMerchant && (
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'center' }}
          gap={{ base: '2', md: '5' }}
          mb="2"
        >
          {!isMerchantFiltered && (
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'stretch', md: 'center' }}
              gap={{ base: '1', md: '3' }}
            >
              <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
                Merchants
              </Text>
              <CommonTabSwitcher
                tabs={SCOPE_TABS}
                activeIndex={merchantScope === MERCHANT_SCOPE.ALL ? 1 : 0}
                onChange={(index) =>
                  onScopeChange(
                    index === 1 ? MERCHANT_SCOPE.ALL : MERCHANT_SCOPE.TOP,
                  )
                }
                isDisabled={isLoading}
                isFullWidth
                mb={0}
              />
            </Flex>
          )}

          <Flex
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'stretch', md: 'center' }}
            gap={{ base: '1', md: '3' }}
          >
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.600">
              Breakdown
            </Text>
            <CommonTabSwitcher
              tabs={DETAIL_TABS}
              activeIndex={isStacked ? 1 : 0}
              onChange={(index) =>
                onDetailChange(
                  index === 1 ? STATION_DETAIL.ALL : STATION_DETAIL.TOTAL,
                )
              }
              isDisabled={isLoading}
              isFullWidth
              mb={0}
            />
          </Flex>

          {summary && (
            <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
              {summary}
            </Text>
          )}
        </Flex>
      )}

      {isSingleMerchant && summary && (
        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500" mb="2">
          {report.stationCount}{' '}
          {report.stationCount === 1 ? 'station' : 'stations'}
        </Text>
      )}

      {!isStacked && !isSingleMerchant && (
        <Text fontSize="xs" color="gray.500" mb="2">
          Station colours are shown only when All stations is selected.
        </Text>
      )}

      {report?.truncated && (
        <Text fontSize="xs" color="orange.500" mb="2">
          Showing the top {report.merchantCount} merchants by revenue out of{' '}
          {report.totalMerchantCount}.
        </Text>
      )}

      {legendEntries.length > 0 && (
        <Flex wrap="wrap" gap={{ base: '2', md: '3' }} mb="3">
          {legendEntries.map((entry) => (
            <Flex key={entry.label} align="center" gap="1">
              <Box w="8px" h="8px" borderRadius="full" bg={entry.color} />
              <Text fontSize={{ base: '10px', md: 'sm' }} color="gray.600">
                {entry.label}
              </Text>
            </Flex>
          ))}
          {hiddenLegendCount > 0 && (
            <Text fontSize={{ base: '10px', md: 'sm' }} color="gray.500">
              +{hiddenLegendCount} more
            </Text>
          )}
        </Flex>
      )}

      <MerchantRevenueStationGraph
        report={report}
        isLoading={isLoading}
        currency={currency}
      />
    </Box>
  );
};

GrossRevenuePerStationCard.propTypes = {
  report: PropTypes.object,
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
  merchantScope: PropTypes.string.isRequired,
  stationDetail: PropTypes.string.isRequired,
  onScopeChange: PropTypes.func.isRequired,
  onDetailChange: PropTypes.func.isRequired,
  isMerchantFiltered: PropTypes.bool,
};

export default React.memo(GrossRevenuePerStationCard);
